import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { IGoalParent } from "@gtmhub/goals/models";
import { localize } from "@gtmhub/localization";
import { GoalsChangedEventArgs } from "@gtmhub/okrs";
import { ICheckinV2 } from "@gtmhub/okrs/metrics/models/models";
import { allowedActionsSync } from "@gtmhub/permissions/utils";
import { ISessionsIdMap, PlanningSessionStatus } from "@gtmhub/sessions";
import { getCurrentUserId, IUser, UserService } from "@gtmhub/users";
import { splitBy, toIdMap } from "@gtmhub/util";
import {
  DraftToImportedGoalMap,
  IDraftGoalState,
  cloneAsImportedGoal,
  IAlignmentLinkState,
  IGoalState,
  IDraftGoalMetricState,
  isDraftOrImportedGoalState,
  IWhiteboardState,
  IWhiteboardLinkState,
  toAlertState,
  IImportedGoalMetricState,
} from "@gtmhub/whiteboards";
import { AlignmentRejectedMessageModal } from "@gtmhub/whiteboards/components/message-modal.component";
import { copy, equals } from "angular";
import { IAlignmentLinkTarget } from "../links/alignment-link";
import { IHeadingState, INoteState, isImportedType, IWhiteboardShapeState } from "../shapes";
import { WhiteboardRenderer } from "../whiteboard-renderer";
import { ChangeHistoryTracker } from "./change-history-tracker";
import { getShapeChanges, ShapeStateConverter } from "./shape-state-converter";
import { WhiteboardStateHolder } from "./whiteboard-state-holder";

const calculateProgress = (metric: IImportedGoalMetricState | IDraftGoalMetricState, actual: number): number => {
  const range = metric.target - metric.initialValue;
  const attainment = (100 * (actual - metric.initialValue)) / range;
  return attainment / 100;
};

const calculateAttainment = (metrics: IImportedGoalMetricState[]): { value: number } => {
  let sum = 0;
  metrics.forEach((m) => (sum += m.attainment.value));
  return { value: sum / metrics.length };
};

export class StateProcessor {
  currentUser: IUser;

  constructor(
    private changeTracker: ChangeHistoryTracker,
    private whiteboardStateHolder: WhiteboardStateHolder,
    private shapeStateConverter: ShapeStateConverter,
    userService: UserService,
    private sessionsIdMap: ISessionsIdMap,
    private alignmentRejectionModal: AlignmentRejectedMessageModal,
    uiErrorHandlingService: UIErrorHandlingService
  ) {
    userService.getUser(getCurrentUserId()).then(
      (user) => (this.currentUser = user),
      (err) => uiErrorHandlingService.handleModal(err, { backdrop: false })
    );
  }

  processPublishedDraft(publishedMap: DraftToImportedGoalMap): void {
    const stateTransaction = this.changeTracker.beginTransaction("Publish draft Objectives");

    for (const [draftGoalId, createdGoal] of Object.entries(publishedMap)) {
      const draftGoal = this.whiteboardStateHolder.findShape<IDraftGoalState>(draftGoalId);
      const importedGoal = cloneAsImportedGoal(draftGoal, createdGoal);
      stateTransaction.updateShape(importedGoal);

      // if this shape was a parent of another one, we need
      // to update the ID of the other shape
      const draftGoalChildren = this.findGoalShapeChildren(draftGoal);
      for (const draftGoalChild of draftGoalChildren) {
        const childParent = draftGoalChild.parent;
        let newChildParent: IGoalParent;

        if (childParent.type === "metric") {
          const metricId = createdGoal.metrics[childParent.id];
          if (metricId) {
            newChildParent = { type: "metric", id: metricId };
          }
        }
        if (!newChildParent) {
          newChildParent = { type: "goal", id: importedGoal.id };
        }

        stateTransaction.applyShapeChanges(draftGoalChild.id, { parent: newChildParent });
      }
      stateTransaction.deleteShape(draftGoal);
    }

    // update links from or to draft goals
    const draftLinks = this.whiteboardStateHolder.findLinks((link) => link.type === "alignment" && (link.source.id in publishedMap || link.target.id in publishedMap));
    for (const draftLink of draftLinks) {
      const newLink = copy(draftLink) as IAlignmentLinkState;

      if (draftLink.source.id in publishedMap) {
        const createdGoal = publishedMap[draftLink.source.id];
        newLink.source.id = createdGoal.id;
        newLink.source.type = "goal";
      }

      if (draftLink.target.id in publishedMap) {
        const createdGoal = publishedMap[draftLink.target.id];
        newLink.target.id = createdGoal.id;
        newLink.target.type = "goal";

        const draftAlignmentLinkTarget = draftLink.target as IAlignmentLinkTarget;
        if (draftAlignmentLinkTarget.metricId) {
          draftAlignmentLinkTarget.metricId = createdGoal.metrics[draftAlignmentLinkTarget.metricId];
        }
      }

      stateTransaction.deleteLink(draftLink).updateLink(newLink);
    }

    stateTransaction.commit({ undo: false });
  }

  updateLink(link: IWhiteboardLinkState, options: { undo: boolean; notify: boolean }): void {
    const stateTransaction = this.changeTracker.beginTransaction("Link change");

    const existingLinks = this.whiteboardStateHolder.findLinks((l) => l.source.id === link.source.id && l.target.id === link.target.id);

    stateTransaction
      .deleteLink(...existingLinks)
      .updateLink(link)
      .commit(options);
  }

  updateShape(shapeId: string, changes: Record<string, unknown>, options: { undo: boolean; notify: boolean }): void {
    const shape = this.whiteboardStateHolder.findShape(shapeId);
    const stateTransaction = this.changeTracker.beginTransaction("Shape change");

    // For now we disallow links from imported goals
    if (shape.type === "draftGoal") {
      // adjust alignment links when changing the parent
      if ("parent" in changes) {
        const parent = changes.parent as IGoalParent;
        if (!equals(parent, shape.parent)) {
          this.deleteShapeLink(shape.id, stateTransaction);

          if (parent && !this.alignmentUpdateSuccessful(shape, parent, changes, stateTransaction)) {
            return;
          }
        }
      }

      // adjust alignment links when changing the metrics, because we might have aligned under a metric
      if ("metrics" in changes) {
        const changedMetrics = toIdMap(changes.metrics as IDraftGoalMetricState[]);
        const invalidMetricLinks = this.whiteboardStateHolder.findLinks((link) => {
          const draftAlignmentLinkTarget = link.target as IAlignmentLinkTarget;
          return link.target.id === shape.id && draftAlignmentLinkTarget.metricId && !changedMetrics[draftAlignmentLinkTarget.metricId];
        });
        stateTransaction.deleteLink(...invalidMetricLinks);
      }
    }

    stateTransaction.applyShapeChanges(shapeId, changes);
    stateTransaction.commit(options);
  }

  deleteSelectedShapes(selectedShapes: IWhiteboardShapeState[]): void {
    const stateTransaction = this.changeTracker.beginTransaction("Delete selected shapes");
    for (const shape of selectedShapes) {
      const shapeLinks = this.whiteboardStateHolder.findShapeLinks(shape.id);
      stateTransaction.deleteShape(shape).deleteLink(...shapeLinks);

      if (shape.type === "draftGoal") {
        const draftGoalChildren = this.findGoalShapeChildren(shape);
        for (const goalChild of draftGoalChildren) {
          stateTransaction.applyShapeChanges(goalChild.id, { parent: undefined });
        }
      }
    }
    stateTransaction.commit();
  }

  updateShapesZIndex(shapeId: string, zIndex: number): void {
    const stateTransaction = this.changeTracker.beginTransaction("Update zIndex");

    const shapes = this.whiteboardStateHolder.findShapes((s) => s).sort((a, b) => a.zIndex - b.zIndex);
    const shapeIndex = shapes.findIndex((shape) => shape.id === shapeId);

    let index = 0;
    for (const shape of shapes) {
      if (zIndex > 0) {
        shape.zIndex = index;
      } else if (zIndex === 0 && index !== shapeIndex) {
        shape.zIndex = index + 1;
      }
      index++;
    }

    shapes[shapeIndex].zIndex = zIndex;
    stateTransaction.updateShape(...shapes).commit({ undo: true, notify: true });
  }

  addShape(shape: IWhiteboardShapeState): void {
    const stateTransaction = this.changeTracker.beginTransaction("Add shape");
    stateTransaction.updateShape(shape);

    if (shape.type === "goal") {
      const childShapes = this.whiteboardStateHolder.findShapes(
        (childShape) => isDraftOrImportedGoalState(childShape) && childShape.parent && childShape.parent.id === shape.id
      ) as IGoalState[];

      for (const childShape of childShapes) {
        stateTransaction.updateLink({ type: "alignment", source: { type: childShape.type, id: childShape.id }, target: { type: shape.type, id: shape.id } });
      }

      const parentShapes = this.whiteboardStateHolder.findShapes((it) => isDraftOrImportedGoalState(it) && it.id === shape.parent?.id) as IGoalState[];
      for (const parentShape of parentShapes) {
        stateTransaction.updateLink({ type: "alignment", source: { type: shape.type, id: shape.id }, target: { type: parentShape.type, id: parentShape.id } });
      }
    }

    stateTransaction.commit();
  }

  convertToGoal(shape: INoteState | IHeadingState, params: { sessionId: string }): void {
    const goalShape = this.shapeStateConverter.createDraftGoalFromNoteOrHeading(shape, params);

    this.changeTracker.beginTransaction("Convert shape to draft Objective").deleteShape(shape).updateShape(goalShape).commit();
  }

  replaceExistingShapesWithAsyncOnes(state: IWhiteboardState): IWhiteboardLinkState[] {
    const associatedLinks: IWhiteboardLinkState[] = [];
    for (let i = 0; i < state.shapes.length; i++) {
      const shape = state.shapes[i];
      if (isImportedType(shape.type)) {
        state.shapes[i] = this.shapeStateConverter.toAsyncOrAlertState(shape);

        const [shapeLinks, otherLinks] = splitBy(state.links, (link) => link.source.id === shape.id || link.target.id === shape.id);
        state.links = otherLinks;
        associatedLinks.push(...shapeLinks);
      }
    }

    return associatedLinks;
  }

  onGoalsChanged(
    args: GoalsChangedEventArgs,
    renderer: WhiteboardRenderer,
    onShapeStateChange: (shapeId: string, changes: Record<string, unknown>, options: { undo: boolean; notify: boolean }) => void
  ): void {
    switch (args.reason) {
      case "goalDeleted": {
        const { goalId } = args.detail;
        const goalShape = this.whiteboardStateHolder.findShape<IGoalState>(goalId);
        const goalLinks = this.whiteboardStateHolder.findShapeLinks(goalId);
        const alertShape = toAlertState(goalShape, "warning", `${localize("objective")} ${goalId} ${localize("was_deleted")}`);
        const alertLinks = goalLinks.map<IAlignmentLinkState>((link) => ({ ...link, target: { type: "goal", id: alertShape.id } } as IAlignmentLinkState));
        this.changeTracker
          .beginTransaction("Objective deleted")
          .deleteShape(goalShape)
          .deleteLink(...goalLinks)
          .updateShape(alertShape)
          .updateLink(...alertLinks)
          .commit({ undo: false });
        this.whiteboardStateHolder.render(renderer);
        break;
      }

      case "goalPatched":
      case "goalUpdated": {
        const { goal } = args.detail;
        const goalShape = this.whiteboardStateHolder.findShape<IGoalState>(goal.id);
        const changes = getShapeChanges(goalShape, Object.assign(this.shapeStateConverter.goalToState(goal), { position: goalShape.position }));
        // metrics are changed in other events
        delete changes.metrics;

        onShapeStateChange(goalShape.id, changes, { undo: false, notify: true });
        break;
      }

      case "metricCreated": {
        const { goalId, metric } = args.detail;
        const goalShape = this.whiteboardStateHolder.findShape<IGoalState>(goalId);
        const metrics = goalShape.metrics.filter((m) => m.id !== metric.id).concat(this.shapeStateConverter.metricToState(metric));
        onShapeStateChange(goalShape.id, { metrics }, { undo: false, notify: true });
        break;
      }

      case "metricDeleted": {
        const { goalId, metricId } = args.detail;
        const goalShape = this.whiteboardStateHolder.findShape<IGoalState>(goalId);
        const metrics = goalShape.metrics.filter((metric) => metric.id !== metricId);
        onShapeStateChange(goalShape.id, { metrics }, { undo: false, notify: true });
        break;
      }

      case "metricPatched": {
        const { goalId, metric } = args.detail;
        const goalShape = this.whiteboardStateHolder.findShape<IGoalState>(goalId);
        const metrics = [...goalShape.metrics];
        const index = metrics.findIndex((m) => m.id === metric.id);
        metrics[index] = this.shapeStateConverter.metricToState(metric);
        onShapeStateChange(goalShape.id, { metrics }, { undo: false, notify: true });
        break;
      }

      case "metricCheckin": {
        const { goalId, metricId, checkin } = args.detail;
        const goalShape = this.whiteboardStateHolder.findShape<IGoalState>(goalId);
        const metrics = [...goalShape.metrics];
        const index = metrics.findIndex((m) => m.id === metricId);
        const metric = metrics[index] as IImportedGoalMetricState;
        metric.attainment = {
          ...metric.attainment,
          value: calculateProgress(metrics[index], checkin.actual || (checkin as ICheckinV2).value),
        };
        const attainment = calculateAttainment(metrics as IImportedGoalMetricState[]);
        onShapeStateChange(goalShape.id, { metrics, attainment }, { undo: false, notify: true });
        break;
      }
    }
  }

  private alignmentUpdateSuccessful(
    currentShape: IWhiteboardShapeState,
    parent: IGoalParent,
    changes: Record<string, unknown>,
    stateTransaction: ChangeHistoryTracker
  ): boolean {
    const currentDraftGoal = this.shapeStateConverter.draftToGoal(currentShape as IDraftGoalState);
    let targetShape: IGoalState;
    if (parent.type === "goal") {
      targetShape = this.whiteboardStateHolder.findShape(parent.id);
    } else {
      targetShape = this.whiteboardStateHolder.findShape((s) => isDraftOrImportedGoalState(s) && s.metrics.some((m) => m.id === parent.id));
    }

    if (targetShape) {
      const targetShapeSessionStatus = this.sessionsIdMap[targetShape.sessionId].status;

      if (targetShapeSessionStatus !== PlanningSessionStatus.OPEN) {
        this.alignmentRejectionModal.open(targetShapeSessionStatus);
        return false;
      }
      if (!allowedActionsSync(this.currentUser, this.sessionsIdMap[targetShape.sessionId].access).includes("create")) {
        this.alignmentRejectionModal.open("no-permissions");
        return false;
      }

      const currentDraftGoalSessionIsParentSessionOrItsChildSession =
        currentDraftGoal.sessionId === targetShape.sessionId || this.sessionsIdMap[currentDraftGoal.sessionId].parentId === targetShape.sessionId;
      if (!currentDraftGoalSessionIsParentSessionOrItsChildSession) {
        changes.sessionId = targetShape.sessionId;
      }

      if (parent.type === "goal") {
        stateTransaction.updateLink({ type: "alignment", source: { type: "draftGoal", id: currentShape.id }, target: { type: targetShape.type, id: parent.id } });
      } else {
        stateTransaction.updateLink({
          type: "alignment",
          source: { type: "draftGoal", id: currentShape.id },
          target: { type: targetShape.type, id: targetShape.id, metricId: parent.id },
        });
      }
    }

    return true;
  }

  private findGoalShapeChildren(shape: IGoalState): IDraftGoalState[] {
    return this.whiteboardStateHolder.findShapes(
      (s) =>
        s.type === "draftGoal" &&
        s.parent &&
        ((s.parent.type === "goal" && s.parent.id === shape.id) || (s.parent.type === "metric" && shape.metrics.some((m) => m.id === s.parent.id)))
    ) as IDraftGoalState[];
  }

  private deleteShapeLink(shapeId: string, stateTransaction: ChangeHistoryTracker): void {
    const existingLinks = this.whiteboardStateHolder.findLinks((link) => link.source.id === shapeId);
    stateTransaction.deleteLink(...existingLinks);
  }
}
